"use client";

import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Drawer, FormControl, Grid,
  IconButton, InputLabel, MenuItem, Paper, Select, Switch, Tab, Table,
  TableBody, TableCell, TableHead, TableRow, TablePagination, Tabs,
  TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";

const IST = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";

const INR = (n) => Number(n || 0).toLocaleString("en-IN");

// ── Account form ──────────────────────────────────────────────────────────────

function AccountDrawer({ open, onClose, existing, token, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm(existing
        ? { name: existing.name, email: existing.email || "", phone: existing.phone || "" }
        : { name: "", email: "", phone: "", opening_balance: "" }
      );
      setErr("");
    }
  }, [open, existing]);

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) return setErr("Name is required.");
    setSaving(true); setErr("");
    try {
      const url = existing ? `${apiBaseUrl}/accounts/${existing._id}` : `${apiBaseUrl}/accounts`;
      const method = existing ? "PUT" : "POST";
      const res = await axiosRequest(method, url, token, form);
      if (res?.data?.status === false || res?.status === false) {
        setErr(res?.data?.message || res?.message || "Failed to save.");
        return;
      }
      onSaved();
      onClose();
    } catch { setErr("Something went wrong."); }
    finally { setSaving(false); }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, p: 3 } }}>
      <Typography variant="h6" fontWeight={700} mb={3}>{existing ? "Edit Account" : "Add Account"}</Typography>
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField fullWidth label="Name *" value={form.name} onChange={f("name")} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Email" value={form.email} onChange={f("email")} type="email" />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Phone Number" value={form.phone} onChange={f("phone")} />
        </Grid>
        {!existing && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Opening Balance ()"
              type="number"
              value={form.opening_balance}
              onChange={f("opening_balance")}
              inputProps={{ min: 0 }}
              helperText="Initial balance credited to this account's ledger"
            />
          </Grid>
        )}
      </Grid>
      <Box mt={4} display="flex" gap={2}>
        <Button fullWidth variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : (existing ? "Update" : "Save")}
        </Button>
        <Button fullWidth variant="outlined" onClick={onClose} disabled={saving}>Cancel</Button>
      </Box>
    </Drawer>
  );
}

// ── Manual ledger entry (add + edit) ─────────────────────────────────────────

function LedgerEntryDialog({ open, onClose, accounts, token, onSaved, existing }) {
  const blank = { account_id: "", type: "CREDIT", amount: "", description: "", transaction_type: "MANUAL", date: "" };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      if (existing) {
        const ISOf = (d) => d ? new Date(d).toISOString().slice(0, 16) : "";
        setForm({
          account_id:       existing.account?._id || existing.account || "",
          type:             existing.type || "CREDIT",
          amount:           existing.amount || "",
          description:      existing.description || "",
          transaction_type: existing.transaction_type || "MANUAL",
          date:             ISOf(existing.date),
        });
      } else {
        setForm(blank);
      }
      setErr("");
    }
  }, [open, existing]);

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.account_id) return setErr("Select an account.");
    if (!form.amount || Number(form.amount) <= 0) return setErr("Enter a valid amount.");
    setSaving(true); setErr("");
    try {
      let res;
      if (existing) {
        res = await axiosRequest("PATCH", `${apiBaseUrl}/account-ledger/${existing._id}`, token, {
          ...form, amount: Number(form.amount),
          date: form.date ? new Date(form.date).toISOString() : undefined,
        });
      } else {
        res = await axiosRequest("POST", `${apiBaseUrl}/account-ledger`, token, {
          ...form, amount: Number(form.amount),
          date: form.date ? new Date(form.date).toISOString() : undefined,
        });
      }
      if (res?.data?.status === false || res?.status === false) { setErr(res?.data?.message || "Failed."); return; }
      onSaved(); onClose();
    } catch { setErr("Something went wrong."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{existing ? "Edit Ledger Entry" : "Add Ledger Entry"}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{err}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Account *</InputLabel>
              <Select value={form.account_id} label="Account *" onChange={f("account_id")}>
                {accounts.map((a) => <MenuItem key={a._id} value={a._id}>{a.name}{a.phone ? ` — ${a.phone}` : ""}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Type *</InputLabel>
              <Select value={form.type} label="Type *" onChange={f("type")}>
                <MenuItem value="CREDIT">Credit</MenuItem>
                <MenuItem value="DEBIT">Debit</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Amount *" type="number" value={form.amount} onChange={f("amount")} />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Transaction Type</InputLabel>
              <Select value={form.transaction_type} label="Transaction Type" onChange={f("transaction_type")}>
                <MenuItem value="MANUAL">Manual</MenuItem>
                <MenuItem value="DEPOSIT">Deposit</MenuItem>
                <MenuItem value="WITHDRAW">Withdraw</MenuItem>
                <MenuItem value="EXPENSE">Expense</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" value={form.description} onChange={f("description")} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Date" type="datetime-local" value={form.date} onChange={f("date")} InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : (existing ? "Update" : "Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AccountManager() {
  const token = useSelector((s) => s.loginSlice.token);
  const [tab, setTab] = useState(0);

  // Accounts state
  const [accounts, setAccounts]       = useState([]);
  const [accLoading, setAccLoading]   = useState(false);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [togglingId, setTogglingId]   = useState(null);

  // Ledger state
  const [ledger, setLedger]           = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerTotals, setLedgerTotals]   = useState({ CREDIT: 0, DEBIT: 0 });
  const [ledgerPage, setLedgerPage]   = useState(0);
  const [ledgerSize, setLedgerSize]   = useState(20);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [filterAccount, setFilterAccount] = useState("");
  const [filterType, setFilterType]   = useState("");
  const [filterFrom, setFilterFrom]   = useState("");
  const [filterTo, setFilterTo]       = useState("");
  const [entryOpen, setEntryOpen]     = useState(false);
  const [editEntry, setEditEntry]     = useState(null);
  const [csvLoading, setCsvLoading]   = useState(false);

  const activeAccounts = accounts.filter(a => a.status !== false);

  const fetchAccounts = async () => {
    setAccLoading(true);
    try {
      const res = await axiosRequest("GET", `${apiBaseUrl}/accounts?all=true`, token);
      setAccounts(res?.data?.data || []);
    } finally { setAccLoading(false); }
  };

  const handleToggleStatus = async (account) => {
    setTogglingId(account._id);
    try {
      await axiosRequest("PUT", `${apiBaseUrl}/accounts/${account._id}`, token, { status: !account.status });
      fetchAccounts();
    } finally { setTogglingId(null); }
  };

  const fetchLedger = async (pg = ledgerPage, sz = ledgerSize) => {
    setLedgerLoading(true);
    try {
      const params = [`page=${pg + 1}`, `size=${sz}`];
      if (filterAccount) params.push(`account_id=${filterAccount}`);
      if (filterType)    params.push(`type=${filterType}`);
      if (filterFrom)    params.push(`date_from=${new Date(filterFrom).toISOString()}`);
      if (filterTo)      params.push(`date_to=${new Date(filterTo).toISOString()}`);
      const res = await axiosRequest("GET", `${apiBaseUrl}/account-ledger?${params.join("&")}`, token);
      const d = res?.data?.data;
      setLedger(d?.list || []);
      setLedgerTotals(d?.totals || { CREDIT: 0, DEBIT: 0 });
      setLedgerTotal(d?.pagination?.totalRecords || 0);
    } finally { setLedgerLoading(false); }
  };

  useEffect(() => { fetchAccounts(); }, []);
  useEffect(() => { if (tab === 1) fetchLedger(); }, [tab]);

  const handleExportCsv = async () => {
    setCsvLoading(true);
    try {
      let url = `${apiBaseUrl}/account-ledger/export-csv?x=1`;
      if (filterAccount) url += `&account_id=${filterAccount}`;
      if (filterType)    url += `&type=${filterType}`;
      if (filterFrom)    url += `&date_from=${new Date(filterFrom).toISOString()}`;
      if (filterTo)      url += `&date_to=${new Date(filterTo).toISOString()}`;
      const res = await fetch(url, { headers: { token } });
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = href; a.download = `account_ledger_${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(href);
    } finally { setCsvLoading(false); }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <AccountBalanceWalletIcon color="primary" fontSize="large" />
          <Typography variant="h4" fontWeight={700}>Account Manager</Typography>
        </Box>
        {tab === 0 && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditTarget(null); setDrawerOpen(true); }}>
            Add Account
          </Button>
        )}
        {tab === 1 && (
          <Box display="flex" gap={1}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setEntryOpen(true)}>Add Entry</Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv} disabled={csvLoading}>
              {csvLoading ? <CircularProgress size={18} /> : "Export CSV"}
            </Button>
          </Box>
        )}
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: "1px solid #e0e0e0" }}>
        <Tab label="Accounts" icon={<PeopleAltIcon />} iconPosition="start" />
        <Tab label="Ledger" icon={<AccountBalanceWalletIcon />} iconPosition="start" />
      </Tabs>

      {/* ── ACCOUNTS TAB ── */}
      {tab === 0 && (
        <>
          {accLoading ? (
            <Box textAlign="center" py={6}><CircularProgress /></Box>
          ) : accounts.length === 0 ? (
            <Paper elevation={0} sx={{ p: 6, textAlign: "center", border: "1px dashed #ccc", borderRadius: 3 }}>
              <PeopleAltIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography color="text.secondary">No accounts yet. Add your first account.</Typography>
            </Paper>
          ) : (
            <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 3, overflow: "hidden" }}>
              <Table>
                <TableHead sx={{ bgcolor: "#1c2433" }}>
                  <TableRow>
                    <TableCell><b>Name</b></TableCell>
                    <TableCell><b>Email</b></TableCell>
                    <TableCell><b>Phone</b></TableCell>
                    <TableCell align="right"><b>Opening Balance</b></TableCell>
                    <TableCell align="right"><b>Current Balance</b></TableCell>
                    <TableCell><b>Created</b></TableCell>
                    <TableCell><b>Status</b></TableCell>
                    <TableCell align="right"><b>Actions</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.map((a) => (
                    <TableRow key={a._id} hover>
                      <TableCell><Typography fontWeight={600}>{a.name}</Typography></TableCell>
                      <TableCell>{a.email || "—"}</TableCell>
                      <TableCell>{a.phone || "—"}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">{INR(a.opening_balance || 0)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          fontWeight={700}
                          color={a.current_balance >= 0 ? "success.main" : "error.main"}
                        >
                          {INR(a.current_balance || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>{IST(a.createdAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={a.status !== false ? "Active" : "Inactive"}
                          size="small"
                          color={a.status !== false ? "success" : "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={a.status !== false ? "Deactivate" : "Activate"}>
                          <Switch
                            size="small"
                            checked={a.status !== false}
                            disabled={togglingId === a._id}
                            onChange={() => handleToggleStatus(a)}
                          />
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => { setEditTarget(a); setDrawerOpen(true); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}

      {/* ── LEDGER TAB ── */}
      {tab === 1 && (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2, bgcolor: "#0f2a17" }}>
                <Typography variant="caption" color="text.secondary">Total Credit</Typography>
                <Typography variant="h6" fontWeight={700} color="success.dark">{INR(ledgerTotals.CREDIT)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2, bgcolor: "#2c141c" }}>
                <Typography variant="caption" color="text.secondary">Total Debit</Typography>
                <Typography variant="h6" fontWeight={700} color="error.dark">{INR(ledgerTotals.DEBIT)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2, bgcolor: "#0f2740" }}>
                <Typography variant="caption" color="text.secondary">Net Balance</Typography>
                <Typography variant="h6" fontWeight={700} color="primary.dark">
                  {INR(ledgerTotals.CREDIT - ledgerTotals.DEBIT)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Filters */}
          <Paper elevation={0} sx={{ p: 2, mb: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Account</InputLabel>
                  <Select value={filterAccount} label="Account" onChange={(e) => setFilterAccount(e.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    {accounts.map((a) => <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select value={filterType} label="Type" onChange={(e) => setFilterType(e.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="CREDIT">Credit</MenuItem>
                    <MenuItem value="DEBIT">Debit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField fullWidth size="small" label="From" type="date" InputLabelProps={{ shrink: true }} value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField fullWidth size="small" label="To" type="date" InputLabelProps={{ shrink: true }} value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
              </Grid>
              <Grid item xs={6} sm={1.5}>
                <Button fullWidth variant="contained" size="small" onClick={() => { setLedgerPage(0); fetchLedger(0, ledgerSize); }}>
                  Search
                </Button>
              </Grid>
              <Grid item xs={6} sm={1.5}>
                <Button fullWidth variant="outlined" size="small" onClick={() => {
                  setFilterAccount(""); setFilterType(""); setFilterFrom(""); setFilterTo("");
                  setLedgerPage(0); setTimeout(() => fetchLedger(0, ledgerSize), 0);
                }}>Reset</Button>
              </Grid>
            </Grid>
          </Paper>

          {ledgerLoading ? (
            <Box textAlign="center" py={6}><CircularProgress /></Box>
          ) : (
            <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 3, overflow: "hidden" }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#1c2433" }}>
                  <TableRow>
                    <TableCell><b>Date</b></TableCell>
                    <TableCell><b>Account</b></TableCell>
                    <TableCell><b>Type</b></TableCell>
                    <TableCell><b>Txn Type</b></TableCell>
                    <TableCell><b>Description</b></TableCell>
                    <TableCell align="right"><b>Amount</b></TableCell>
                    <TableCell><b>Created By</b></TableCell>
                    <TableCell align="center"><b>Edit</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledger.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>No entries found.</TableCell>
                    </TableRow>
                  ) : ledger.map((e) => (
                    <TableRow key={e._id} hover>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{IST(e.date)}</TableCell>
                      <TableCell>
                        <Typography fontWeight={600} variant="body2">{e.account?.name || "—"}</Typography>
                        {e.account?.phone && <Typography variant="caption" color="text.secondary">{e.account.phone}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={e.type}
                          size="small"
                          color={e.type === "CREDIT" ? "success" : "error"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={e.transaction_type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{e.description || "—"}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={700} color={e.type === "CREDIT" ? "success.main" : "error.main"}>
                          {e.type === "CREDIT" ? "+" : "−"}{INR(e.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>{e.created_by?.name || e.created_by?.email || "—"}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit entry">
                          <IconButton size="small" onClick={() => setEditEntry(e)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Divider />
              <TablePagination
                component="div"
                count={ledgerTotal}
                page={ledgerPage}
                onPageChange={(_, p) => { setLedgerPage(p); fetchLedger(p, ledgerSize); }}
                rowsPerPage={ledgerSize}
                onRowsPerPageChange={(e) => { const s = parseInt(e.target.value, 10); setLedgerSize(s); setLedgerPage(0); fetchLedger(0, s); }}
                rowsPerPageOptions={[10, 20, 50]}
              />
            </Paper>
          )}
        </>
      )}

      {/* Drawers / Dialogs */}
      <AccountDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        existing={editTarget}
        token={token}
        onSaved={fetchAccounts}
      />
      <LedgerEntryDialog
        open={entryOpen}
        onClose={() => setEntryOpen(false)}
        accounts={activeAccounts}
        token={token}
        onSaved={() => fetchLedger(ledgerPage, ledgerSize)}
      />
      <LedgerEntryDialog
        open={!!editEntry}
        onClose={() => setEditEntry(null)}
        existing={editEntry}
        accounts={activeAccounts}
        token={token}
        onSaved={() => fetchLedger(ledgerPage, ledgerSize)}
      />
    </Box>
  );
}
